"use client";

import { WORKPLACE_REQUEST_RESERVATION_MAX_DATES } from "@gettactic/api";
import { HookFormCalendarInput } from "@gettactic/components/src/form/FormCalendarInput";
import { HookFormTimeSelect } from "@gettactic/components/src/form/TimeSelect";
import { Button } from "@gettactic/components/src/primitives/button";
import { addDays } from "date-fns";
import { Plus, Trash2 } from "lucide-react";
import React from "react";
import { type UseFormReturn, useFieldArray } from "react-hook-form";
import type { ReservationOverrideFormValues } from "./reservationOverrideForm";

interface ReservationPeriodsFieldProps {
  form: UseFormReturn<ReservationOverrideFormValues>;
  /** Whether core would book a series; days can't be added alongside one. */
  hasRecurrence: boolean;
  /** Where the times are read, spelled out under the list. */
  timeZoneNote: string;
}

/**
 * The days this approval books, one row each.
 *
 * The rows carry no per-control labels: at fifteen days they would be read
 * once and then be noise, and the placeholders say the same thing where it is
 * actually needed. The last row can't be removed — an approval that books
 * nothing is a rejection, not an empty list.
 */
export const ReservationPeriodsField: React.FC<
  ReservationPeriodsFieldProps
> = ({ form, hasRecurrence, timeZoneNote }) => {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "periods"
  });
  const periodsError = form.formState.errors.periods;

  const addDay = () => {
    // The day after the last one, at the same hours: an approver adding a row
    // is extending a booking, not starting an unrelated one.
    const last = form.getValues("periods").at(-1);
    append({
      date: last ? addDays(last.date, 1) : new Date(),
      startTime: last?.startTime ?? "",
      endTime: last?.endTime ?? ""
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="form-field-label font-medium text-[13px] text-neutral-900">
        Days
      </span>
      {fields.map((field, index) => (
        <div key={field.id} className="flex items-start gap-2">
          <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-3">
            <HookFormCalendarInput
              form={form}
              name={`periods.${index}.date`}
              mode="single"
              variant="outlined"
              // A single date needs no Apply step: picking the day is the answer.
              setValueOnApply={false}
              // A repeating booking is anchored on this day: the rule names the
              // weekday it was built from, and the approval override carries no
              // recurrence to move with it. Moving the day here would leave the
              // series pointing at one day and the booking at another, so the
              // times stay editable and the day does not.
              isReadOnly={hasRecurrence}
            />
            <HookFormTimeSelect
              form={form}
              name={`periods.${index}.startTime`}
              variant="outlined"
              placeholder="Start time"
            />
            <HookFormTimeSelect
              form={form}
              name={`periods.${index}.endTime`}
              variant="outlined"
              placeholder="End time"
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`Remove day ${index + 1}`}
            iconLeft={<Trash2 className="h-4 w-4" />}
            disabled={fields.length === 1}
            onClick={() => remove(index)}
          >
            Remove
          </Button>
        </div>
      ))}

      {typeof periodsError?.message === "string" ? (
        <p className="text-red-600 text-xs">{periodsError.message}</p>
      ) : null}

      {hasRecurrence ? (
        <p className="text-neutral-500 text-xs">
          This booking repeats, so it covers a single day here and that day is
          fixed — Tactic expands the series when it books. The times are still
          yours to adjust.
        </p>
      ) : (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconLeft={<Plus className="h-4 w-4" />}
            disabled={fields.length >= WORKPLACE_REQUEST_RESERVATION_MAX_DATES}
            onClick={addDay}
          >
            Add a day
          </Button>
        </div>
      )}

      <p className="text-neutral-500 text-xs">{timeZoneNote}</p>
    </div>
  );
};
